import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalComponent, PdfViewerComponent } from '@app/components';
import { getAllFileTypes } from '@app/functions/files/get-all-file-types';
import { getImageFileTypes } from '@app/functions/files/get-image-file-types';
import { validateForm } from '@app/functions/validate-form';
import { File } from '@app/models/common/file.model';
import { Employee } from '@app/models/employee/employee.model';
import { Task } from '@app/models/tasks/task.model';
import { AuthService, FileHelperService, NotifyService, TasksService } from '@app/services';

const onboardingTaskType = 'onboarding';

@Component({
    selector: 'app-deprecated-task-checklist',
    templateUrl: './deprecated-task-checklist.template.html',
    styleUrls: ['./deprecated-task-checklist.style.scss'],
})
export class DeprecatedTaskChecklistComponent {
    @ViewChild('confirmTaskDeleteDialog', { static: true })
    confirmTaskDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('taskDetailsModal') taskDetailsModal: DeprecatedModalComponent;
    @ViewChild('container') container: ElementRef;
    @ViewChild('pdfPreviewModal', { static: true }) pdfPreviewModal: DeprecatedModalComponent;
    @ViewChild('pdfViewer', { static: true }) pdfPreview: PdfViewerComponent;

    @Output() updateTasks: EventEmitter<void> = new EventEmitter<void>();
    @Output() showTaskModal: EventEmitter<Task> = new EventEmitter<Task>();
    @Output() onScroll: EventEmitter<void> = new EventEmitter<void>();
    @Output() onLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output() reminded: EventEmitter<void> = new EventEmitter<void>();
    @Output() deleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() viewed: EventEmitter<void> = new EventEmitter<void>();

    @Input() tasks: Task[] = [];
    @Input() employee: Employee;
    @Input() showTaskModalOnEdit = false;
    @Input() onDashboard = false;

    currentTask: Task;
    isAdmin = false;
    allowedImgTypes = getImageFileTypes();
    allowedFileTypes = getAllFileTypes();

    constructor(
        private onboardingTasksService: TasksService,
        private router: Router,
        protected route: ActivatedRoute,
        private notify: NotifyService,
        private auth: AuthService,
        private fileHelper: FileHelperService
    ) {
        if (this.auth.isAdmin()) {
            this.isAdmin = true;
        }
    }

    onRemind(task: Task): void {
        this.onLoading.emit(true);
        task.sendReminder()
            .then(() => {
                this.notify.success('Reminder sent');
                this.onLoading.emit(false);
                this.reminded.emit();
            })
            .catch((err) => {
                this.notify.error(err.message);
                this.onLoading.emit(false);
            });
    }

    onEdit(task: Task): void {
        if (this.showTaskModalOnEdit) {
            this.showTaskModal.emit(task);
            return;
        }
        this.router.navigate([String(task.id)], { relativeTo: this.route });
    }

    onView(task: Task): void {
        this.viewed.emit();
        this.currentTask = task;
        this.taskDetailsModal.show();
    }

    onDone(): void {
        this.taskDetailsModal.onClose();
    }

    onCancel(): void {}

    onDelete(task: Task): void {
        this.setTaskDeleteDialogHeaderAndDescription(task);

        this.confirmTaskDeleteDialog.show().then((confirm) => {
            if (!confirm) {
                return;
            }

            this.onLoading.emit(true);
            this.onboardingTasksService.deleteTask(task, this.employee).then(() => {
                this.notify.success('Task has been deleted');
                this.updateTasks.emit();
                this.deleted.emit();
            });
        });
    }

    onFileSelected(files: FileList, task: Task): void {
        this.onLoading.emit(true);
        this.onboardingTasksService.attachFile(files, task).then(() => {
            this.updateTasks.emit();
        });
    }

    onFileRemoved(task: Task): void {
        this.onLoading.emit(true);

        if (task.isComplete()) {
            task.completedAt = null;
        }
        this.onboardingTasksService.removeFile(task).then(() => {
            this.updateTasks.emit();
        });
    }

    onToggleTaskComplete(event: MouseEvent, task: Task, form: NgForm): void {
        validateForm(form);
        if (form.invalid) {
            (event.target as HTMLInputElement).checked = task.isComplete();
            return;
        }

        this.onLoading.emit(true);
        this.onboardingTasksService.toggleCompletion(task).then(() => {
            this.updateTasks.emit();
        });
    }

    onScrollContainer(): void {
        this.onScroll.emit();
    }

    async viewFile(file: File): Promise<void> {
        // If it's an image, download this file for local viewing
        if (this.allowedImgTypes.includes(`.${file.type}`)) {
            this.fileHelper.saveFile(file);
            return;
        }

        // otherwise, use pdf viewer
        await this.pdfPreview.setSource(this.fileHelper.path(file.id));
        this.pdfPreviewModal.show();
    }

    private setTaskDeleteDialogHeaderAndDescription(task: Task): void {
        const deleteDialogheader = `Delete ${this.employee.firstName}'s`;
        this.confirmTaskDeleteDialog.header =
            task.taskType === onboardingTaskType
                ? deleteDialogheader + ' onboarding task'
                : deleteDialogheader + ' offboarding task';

        this.confirmTaskDeleteDialog.description = `Are you sure you want to delete ${task.name}?`;
    }
}
